'use client';

import Link from '@eliadmin/core/src/components/link';
import { Popover, PopoverContent, PopoverTrigger } from '@eliadmin/core/src/components/ui/popover';
import { Sheet, SheetContent, SheetTrigger } from '@eliadmin/core/src/components/ui/sheet';
import { SignInDialog } from "@eliadmin/core/src/components/dialog/lazy-dialogs";
import { prefetchSocialAuthProviders } from '@eliadmin/core/src/components/auth/social-auth-providers-cache';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useMediaQuery } from '@eliadmin/core/src/hooks/use-media-query';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { LuUser } from 'react-icons/lu';
import { useState } from 'react';

export interface ProfileLinkItems {
  key: string;
  icon: React.ReactNode;
  name: string;
  href: string | (() => string);
}

interface ProfileLinkProps {
  icon: React.ReactNode;
  name: string;
  link: string | (() => string);
  lastItem?: boolean;
  close: () => void;
  logOut?: boolean;
}

export const HeaderProfileButton = () => {
  const { t } = useTranslations();

  const me = useCustomerStore(s => s.me);
  const locale = useLocaleStore(s => s.currentLocale);
  const isInitialized = useCustomerStore(s => s.isInitialized);

  const openDialog = useDialogStore(s => s.open);

  const warmSocialAuthProviders = () => {
    void prefetchSocialAuthProviders(locale);
  };
  const [isOpen, setIsOpen] = useState(false);

  const handleSignInClick = () => {
    warmSocialAuthProviders();
    openDialog({
      id: SignInDialog.id,
      content: <SignInDialog />,
    });
  };

  const isMobile = useMediaQuery('(max-width: 640px)');

  const Wrapper = isMobile ? Sheet : Popover;
  const Content = isMobile ? SheetContent : PopoverContent;
  const Trigger = isMobile ? SheetTrigger : PopoverTrigger;

  const ProfileContent = () => (
    <>
      {HEADER_CONFIG.profileLinks.map((item, index) => {
        if (item.href) {
          return (
            <ProfileLink
              key={index}
              icon={item.icon}
              name={item.name}
              link={item.href}
              lastItem={index === HEADER_CONFIG.profileLinks.length - 1}
              logOut={index === HEADER_CONFIG.profileLinks.length - 1}
              close={() => setIsOpen(false)}
            />
          );
        }
        return <CustomSeparator key={index} />;
      })}
    </>
  );

  return (
    <>
      {me && isInitialized ? (
        <Wrapper open={isOpen} onOpenChange={setIsOpen}>
          <Trigger className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 font-heading text-[15px] font-normal text-white/50 transition-all hover:bg-white/25">
            {me?.customer.name.charAt(0)}
            {me?.customer.surname.charAt(0)}
          </Trigger>
          <Content
            align="center"
            side="bottom"
            sideOffset={18}
            className="flex flex-col items-start justify-start gap-2 rounded-none bg-white p-4"
          >
            <ProfileContent />
          </Content>
        </Wrapper>
      ) : (
        <>
          <button
            type="button"
            aria-label={t('text190')}
            onClick={() => handleSignInClick()}
            onFocus={warmSocialAuthProviders}
            onMouseEnter={warmSocialAuthProviders}
            onTouchStart={warmSocialAuthProviders}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-white/20 md:hidden"
          >
            <LuUser size={18} className="text-white/20" />
          </button>
          <Button
            className="hidden md:flex"
            variant={'outline'}
            onClick={() => handleSignInClick()}
            onFocus={warmSocialAuthProviders}
            onMouseEnter={warmSocialAuthProviders}
            onTouchStart={warmSocialAuthProviders}
          >
            {t('text190')}
          </Button>
        </>
      )}
    </>
  );
};

function ProfileLink({ icon, name, link, lastItem, close, logOut }: ProfileLinkProps) {
  const me = useCustomerStore(s => s.me);
  const isCustomerStoreInitialized = useCustomerStore(s => s.isInitialized);
  let href = typeof link === 'function' ? link() : link;

  if (
    !me &&
    isCustomerStoreInitialized &&
    (href === ROUTES.PROFILE() || href === ROUTES.FAVORITES())
  ) {
    href = ROUTES.SIGN_IN();
  }

  const { t } = useTranslations();
  const className = cn(
    'flex w-full items-center justify-start gap-3 px-4 py-2 text-black',
    'group transition-all hover:bg-[#F6F6F6] hover:text-primary',
    lastItem && 'opacity-70',
  );
  const content = (
    <>
      {icon}
      <span className={cn('text-base font-semibold text-black', 'group-hover:text-primary')}>
        {t(name)}
      </span>
    </>
  );

  if (logOut) {
    return (
      <a href={href} className={className} onClick={() => close()}>
        {content}
      </a>
    );
  }

  return (
    <Link prefetch={true} href={href} className={className} onClick={() => close()}>
      {content}
    </Link>
  );
}

function CustomSeparator() {
  return <Separator className="h-[1px] w-full bg-border" />;
}
